import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X, Plus, Calendar } from "lucide-react";
import { campaignAPI } from "../services/api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin: 0;
  font-family: 'Prompt', sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  padding: 0.5rem;
  border-radius: 0.25rem;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #E5E7EB;
  padding: 0 1.5rem;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.active ? '#00B5B8' : '#6B7280'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#00B5B8' : 'transparent'};
  font-family: 'Prompt', sans-serif;
  
  &:hover {
    color: #00B5B8;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;
  font-family: 'Prompt', sans-serif;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ImagePreview = styled.img`
  margin-top: 1rem;
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 400;
  color: #404E67;
  font-family: 'Prompt', sans-serif;

  &::after {
    content: ${props => props.required ? '" *"' : '""'};
    color: #EF4444;
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 14px;
  font-family: 'Prompt', sans-serif;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Prompt', sans-serif;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 14px;
  font-family: 'Prompt', sans-serif;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 14px;
  font-family: 'Prompt', sans-serif;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  font-family: 'Prompt', sans-serif;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #00B5B8;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #000000;
  margin: 1.5rem 0 1rem 0;
  font-family: 'Prompt', sans-serif;
`;


const AddItemRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 0.5rem;
`;

const AddButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Prompt', sans-serif;
  min-width: 40px;
  height: fit-content;

  &:hover {
    background-color: #1F2937;
  }
`;

const HelpText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #404E67;
  text-align: center;
  margin-top: 0.5rem;
  padding: 1rem;
  background-color: #F3F4F6;
  border-radius: 0.375rem;
  font-family: 'Prompt', sans-serif;
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #E5E7EB;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #404E67;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Prompt', sans-serif;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Prompt', sans-serif;
  flex: 1;

  &:hover {
    background-color: #0891B2;
  }
`;

const UserManagementModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    prefix: "",
    firstName: "",
    lastName: "",
    username: "",
    password: "",
    email: "",
    phone: "",
    gender: "ชาย",
    idCard: "",
    birthDate: "",
    type: "หมอ",
    field: "",
    education: "",
    expertise: "",
    history: "",
    languages: [],
    menuLevel: "ทั้งหมด (อบจ)",
    companies: [],
    isSuperAdmin: false,
    isAdminDoctor: false,
    isNurse: false,
    isManager: false,
    isAnalyze: false
  });

  const [companiesList, setCompaniesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        prefix: "",
        firstName: "",
        lastName: "",
        username: "",
        password: "",
        email: "",
        phone: "",
        gender: "ชาย",
        idCard: "",
        birthDate: "",
        type: "หมอ",
        field: "",
        education: "",
        expertise: "",
        history: "",
        languages: [],
        menuLevel: "ทั้งหมด (อบจ)",
        companies: [],
        isSuperAdmin: false,
        isAdminDoctor: false,
        isNurse: false,
        isManager: false,
        isAnalyze: false
      });
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await campaignAPI.getList(1, '');
      if (response.result && response.list && Array.isArray(response.list)) {
        const uniqueCompanies = [...new Set(response.list.map(item => item.name).filter(name => name && name !== '-'))];
        setCompaniesList(uniqueCompanies);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [selectedImage, setSelectedImage] = useState(null);

  const handleLanguageChange = (value) => {
    setFormData(prev => {
      const languages = prev.languages.includes(value)
        ? prev.languages.filter(l => l !== value)
        : [...prev.languages, value];
      return { ...prev, languages };
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    if (onSave) {
      const roles = [];
      if (formData.isAdminDoctor) roles.push('admin_doctor');
      if (formData.isNurse) roles.push('nurse');
      if (formData.isManager) roles.push('manager');
      if (formData.isAnalyze) roles.push('analyze');

      const apiData = {
        username: formData.username || '',
        password: formData.password || '',
        nameprefix: formData.prefix || 'นาย',
        name: formData.firstName || '',
        lastname: formData.lastName || '',
        email: formData.email || 'test@gmail.com',
        tel: formData.phone || '0000000000',
        nid: formData.idCard || '1111111111111',
        birthday: (() => {
          if (!formData.birthDate) return '2012-03-03';
          const parts = formData.birthDate.split('-');
          if (parts.length === 3 && parseInt(parts[0]) > 2500) {
            return `${parseInt(parts[0]) - 543}-${parts[1]}-${parts[2]}`;
          }
          return formData.birthDate;
        })(),
        gender: formData.gender === 'ชาย' ? '1' : (formData.gender === 'หญิง' ? '2' : '0'),
        image: '',
        typeconsult: formData.type || 'แพทย์',
        typebranch: formData.field || 'อายุรเวท',
        hospitalhistory: '',
        history: '',
        speciallist: '',
        language: 'th,en',
        education: '',
        campaignid: '0',
        level: formData.isSuperAdmin ? '1' : '2',
        role: roles
      };

      onSave(apiData);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>จัดการข้อมูลที่ปรึกษา</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGrid>
            

            <FormGroup>
              <Label required>คำนำหน้าชื่อ</Label>
              <Input
                type="text"
                value={formData.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>ชื่อ</Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>นามสกุล</Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>อีเมล</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>โทร</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>เพศ</Label>
              <Select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
              >
                <option value="ชาย">ชาย</option>
                <option value="หญิง">หญิง</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label required>เลขบัตรประชาชน/เลขบัตรประกันสังคม/passport สำหรับชาวต่างชาติ</Label>
              <Input
                type="text"
                value={formData.idCard}
                onChange={(e) => handleInputChange('idCard', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>วันเกิด (พ.ศ.)</Label>
              <Input
                type="text"
                value={
                  formData.birthDate
                    ? formData.birthDate.split("-").reverse().join("/")
                    : ""
                }
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d/]/g, "");
                  if (value.length > 10) value = value.substring(0, 10);
                  const parts = value.split("/");
                  if (
                    parts.length === 3 &&
                    parts[0].length === 2 &&
                    parts[1].length === 2 &&
                    parts[2].length === 4
                  ) {
                    handleInputChange("birthDate", `${parts[2]}-${parts[1]}-${parts[0]}`);
                  } else {
                    const currentDate = (formData.birthDate || "2000-01-01").split("-");
                    if (parts[0]) currentDate[2] = parts[0].padStart(2, "0");
                    if (parts[1]) currentDate[1] = parts[1].padStart(2, "0");
                    if (parts[2]) currentDate[0] = parts[2];
                    handleInputChange("birthDate", currentDate.join("-"));
                  }
                }}
                placeholder="วว/ดด/ปปปป (เช่น 01/01/2545)"
                maxLength={10}
              />
            </FormGroup>

            <FormGroup>
              <Label required>ประเภท</Label>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <option value="หมอ">หมอ</option>
                <option value="พยาบาล">พยาบาล</option>
                <option value="เจ้าหน้าที่อื่น">เจ้าหน้าที่อื่น</option>
                <option value="manager">manager</option>
                <option value="analyze">analyze</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label required>การศึกษา</Label>
              <TextArea
                value={formData.education}
                onChange={(e) => handleInputChange('education', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>ความชำนาญเฉพาะทาง</Label>
              <TextArea
                value={formData.expertise}
                onChange={(e) => handleInputChange('expertise', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>ประวัติ</Label>
              <TextArea
                value={formData.history}
                onChange={(e) => handleInputChange('history', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label required>ชื่อผู้ใช้ (Username)</Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="ตัวอย่าง: mfuc001"
              />
            </FormGroup>

            <FormGroup>
              <Label required>รหัสผ่าน (Password)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="รหัสผ่านสำหรับเข้าสู่ระบบ"
              />
            </FormGroup>

            <FormGroup>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <CheckboxRow>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.isSuperAdmin || false}
                      onChange={() => handleInputChange('isSuperAdmin', !formData.isSuperAdmin)}
                    />
                    Super Admin
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.isAdminDoctor || false}
                      onChange={() => handleInputChange('isAdminDoctor', !formData.isAdminDoctor)}
                    />
                    Admin Doctor
                  </CheckboxLabel>
                </CheckboxRow>
                <CheckboxRow>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.isNurse || false}
                      onChange={() => handleInputChange('isNurse', !formData.isNurse)}
                    />
                    พยาบาล
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.isManager || false}
                      onChange={() => handleInputChange('isManager', !formData.isManager)}
                    />
                    Manager
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.isAnalyze || false}
                      onChange={() => handleInputChange('isAnalyze', !formData.isAnalyze)}
                    />
                    Analyze
                  </CheckboxLabel>
                </CheckboxRow>
              </div>
            </FormGroup>

          </FormGrid>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>
            ยกเลิก
          </CancelButton>
          <SaveButton onClick={handleSave}>
            เพิ่ม
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default UserManagementModal;